import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus, Truck, Bus, Car, Tent, Pencil, Trash2, CheckCircle2,
  Loader2, Search, Zap, Mountain, Droplets, Sun, ShowerHead, X,
  Caravan, Container, Link2, Camera, TreePine, Waves, Snowflake,
  Shell, Route as RouteIcon, Archive, RotateCcw, Navigation2, AlertTriangle, Info
} from 'lucide-react';
import { AppUser, Vehicle, VehicleCategory, VehicleEquipment, TowVehicleInfo, TerrainPreference } from '../../types';
import {
  subscribeToVehicles, addVehicle, updateVehicle, deleteVehicle,
  setDefaultVehicle, fetchVehicleDataFromSVV, updateTowVehicle, removeTowVehicle,
  uploadVehiclePhotos, retireVehicle, unretireVehicle
} from '../../services/vehicleService';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebaseService';
import { getVehicleCategoryColor, isLightColor } from '../../utils/vehicleMarkerHtml';
import { getVehicleAvatar, getTowVehicleAvatar } from '../../utils/vehicleAvatars';
import { colorClasses } from '../../utils/tailwindColorClasses';
import { pickPhoto } from '../../services/cameraService';

// ── Category metadata ────────────────────────────────────────────────

const CATEGORY_META: Record<VehicleCategory, { icon: React.ElementType; color: string }> = {
  motorhome:      { icon: Truck,     color: 'blue' },
  campervan:      { icon: Bus,       color: 'purple' },
  caravan:        { icon: Caravan,   color: 'teal' },
  camper_trailer: { icon: Container, color: 'rose' },
  rooftent:       { icon: Tent,      color: 'orange' },
  offroad:        { icon: Mountain,  color: 'lime' },
  car:            { icon: Car,       color: 'slate' },
};

const CATEGORIES: VehicleCategory[] = ['motorhome', 'campervan', 'caravan', 'camper_trailer', 'rooftent', 'offroad', 'car'];

// ── Equipment toggles config ─────────────────────────────────────────

const EQUIPMENT_KEYS: { key: keyof VehicleEquipment; icon: React.ElementType; color: string; motorOnly?: boolean }[] = [
  { key: 'is_ev',             icon: Zap,         color: 'blue',   motorOnly: true },
  { key: 'four_wheel_drive',  icon: Mountain,    color: 'amber',  motorOnly: true },
  { key: 'fixed_toilet',      icon: ShowerHead,  color: 'violet' },
  { key: 'grey_water_tank',   icon: Droplets,    color: 'cyan' },
  { key: 'solar_panel',       icon: Sun,         color: 'yellow' },
];

/** Trailers don't have their own motor — filter out EV/4WD */
const TRAILER_CATEGORIES: VehicleCategory[] = ['caravan', 'camper_trailer'];

const DEFAULT_EQUIPMENT: VehicleEquipment = {
  is_ev: false,
  four_wheel_drive: false,
  fixed_toilet: false,
  grey_water_tank: false,
  solar_panel: false,
};

// ── Terrain preferences config ──────────────────────────────────────

const TERRAIN_OPTIONS: { key: TerrainPreference; icon: React.ElementType; color: string }[] = [
  { key: 'paved',    icon: RouteIcon, color: 'stone' },
  { key: 'gravel',   icon: Mountain,  color: 'amber' },
  { key: 'forest',   icon: TreePine,  color: 'emerald' },
  { key: 'sand',     icon: Shell,     color: 'yellow' },
  { key: 'snow',     icon: Snowflake, color: 'sky' },
  { key: 'mountain', icon: Mountain,  color: 'violet' },
  { key: 'coastal',  icon: Waves,     color: 'cyan' },
];

// ── Section Divider ─────────────────────────────────────────────────

const SectionDivider: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex items-center gap-2 pt-2">
    <div className="h-px flex-1 bg-amber-500/20" />
    <span className="text-4xs font-black text-amber-400 uppercase tracking-widest">
      {label}
    </span>
    <div className="h-px flex-1 bg-amber-500/20" />
  </div>
);

// ── Props ────────────────────────────────────────────────────────────

interface GarageTabProps {
  user: AppUser;
}

// ── Component ────────────────────────────────────────────────────────

const GarageTab: React.FC<GarageTabProps> = ({ user }) => {
  const { t } = useTranslation();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [showMarkerPreview, setShowMarkerPreview] = useState<string | null>(null); // vehicleId or null

  const handleToggleVehicleMarker = async (enable: boolean) => {
    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, { vehicleMarkerEnabled: enable });
    } catch (err) {
      console.error('Failed to toggle vehicle marker:', err);
    }
  };

  const handleToggleShareVehicleOnSpots = async (enable: boolean) => {
    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, { shareVehicleOnSpots: enable });
    } catch (err) {
      console.error('Failed to toggle share vehicle on spots:', err);
    }
  };

  const shareVehicleOnSpots = user.shareVehicleOnSpots !== false;

  const MARKER_COLOR_PRESETS = [
    '#1e1e1e', '#f5f5f5', '#6b7280', '#dc2626', '#2563eb',
    '#16a34a', '#eab308', '#ea580c', '#7c3aed', '#d4a574',
  ];

  const handleMarkerColorChange = async (vehicleId: string, color: string | null) => {
    try {
      await updateVehicle(user.uid, vehicleId, { marker_color: color || undefined });
    } catch (err) {
      console.error('Failed to update marker color:', err);
    }
  };


  useEffect(() => {
    if (!user.uid) return;
    return subscribeToVehicles(user.uid, setVehicles);
  }, [user.uid]);

  const handleEdit = (v: Vehicle) => {
    setEditingVehicle(v);
    setShowForm(true);
  };

  const handleDelete = async (vehicleId: string) => {
    await deleteVehicle(user.uid, vehicleId);
    setDeleteConfirmId(null);
  };

  const handleSetDefault = async (vehicleId: string) => {
    await setDefaultVehicle(user.uid, vehicleId);
  };

  const handleRetire = async (vehicleId: string) => {
    await retireVehicle(user.uid, vehicleId);
  };

  const handleUnretire = async (vehicleId: string) => {
    await unretireVehicle(user.uid, vehicleId);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingVehicle(null);
  };

  // ── Empty state ──────────────────────────────────────────────────

  if (vehicles.length === 0 && !showForm) {
    return (
      <div className="space-y-6 animate-in slide-in-from-bottom-2 duration-300 pb-12">
        <div className="text-center py-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-500/10 border border-orange-500/20 mb-4">
            <Truck size={28} className="text-orange-400" />
          </div>
          <h3 className="text-sm font-black text-[var(--text-primary)] uppercase tracking-widest mb-2">
            {t('profile.garage.title')}
          </h3>
          <p className="text-2xs text-[var(--text-muted)] font-bold max-w-[260px] mx-auto mb-6 leading-relaxed">
            {t('profile.garage.empty_state_detailed')}
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-3 rounded-2xl font-black text-tiny uppercase tracking-widest transition-all active:scale-95"
          >
            <Plus size={16} />
            {t('profile.garage.add_vehicle')}
          </button>
        </div>
      </div>
    );
  }

  // ── Vehicle list + form ──────────────────────────────────────────

  return (
    <div className="space-y-4 animate-in slide-in-from-bottom-2 duration-300 pb-12">
      <div className="rounded-2xl border border-[var(--border-primary)] bg-[var(--bg-surface-alt)] p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-3xs font-black text-[var(--text-primary)] uppercase tracking-widest">
              {t('profile.garage.share_on_spots_title', 'Vis bil på plasser')}
            </p>
            <p className="text-tiny text-[var(--text-muted)] mt-1 leading-relaxed">
              {t(
                'profile.garage.share_on_spots_desc',
                'Når du sjekker inn, kan andre se bilde og type fra garasjen din — så de vet hvilke biler som har klart veien.',
              )}
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={shareVehicleOnSpots}
            onClick={() => handleToggleShareVehicleOnSpots(!shareVehicleOnSpots)}
            className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors ${
              shareVehicleOnSpots ? 'bg-emerald-500' : 'bg-[var(--bg-input)] border border-[var(--border-primary)]'
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                shareVehicleOnSpots ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Vehicle Cards */}
      {vehicles.filter(v => !v.is_retired).map((v, index) => {
        const meta = CATEGORY_META[v.category] || CATEGORY_META.motorhome;
        const Icon = meta.icon;
        const isConfirmingDelete = deleteConfirmId === v.id;
        const hasPhoto = !!v.photo_url;

        return (
          <div
            key={v.id}
            className={`relative overflow-hidden rounded-2xl border transition-all animate-in fade-in slide-in-from-bottom-2 duration-300 ${
              v.is_default ? 'border-emerald-500/30' : 'border-[var(--border-primary)]'
            }`}
            style={{ animationDelay: `${index * 80}ms` }}
          >
            {/* ── Photo-backed showcase card ── */}
            {hasPhoto ? (
              <div className="relative">
                {/* Hero image */}
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={v.photo_url}
                    alt={v.nickname}
                    className="w-full h-full object-cover"
                  />
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/60 to-transparent" />

                  {/* Top-left: category pill */}
                  <div className={`absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/50 backdrop-blur-sm border border-[var(--border-secondary)]`}>
                    <Icon size={12} className={colorClasses(meta.color).text400} />
                    <span className="text-4xs font-black text-white uppercase tracking-widest">
                      {t(`profile.garage.categories.${v.category}`)}
                    </span>
                  </div>

                  {/* Top-right: active badge */}
                  {v.is_default && (
                    <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-1 rounded-full text-4xs font-black uppercase tracking-widest bg-emerald-500/20 backdrop-blur-sm text-emerald-400 border border-emerald-500/30">
                      <CheckCircle2 size={9} />
                      {t('profile.garage.active_badge')}
                    </span>
                  )}

                  {/* Bottom overlay content */}
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-[15px] font-black text-white tracking-tight">{v.nickname}</h3>
                    {v.make_model && (
                      <p className="text-tiny text-[var(--text-secondary)] font-bold">{v.make_model}</p>
                    )}
                    {/* Stats badges */}
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {v.length_cm && (
                        <span className="px-2 py-0.5 rounded-lg text-4xs font-black uppercase tracking-widest bg-[var(--bg-input)] backdrop-blur-sm text-[var(--text-secondary)] border border-[var(--border-secondary)]">
                          L: {v.length_cm} cm
                        </span>
                      )}
                      {v.width_cm && (
                        <span className="px-2 py-0.5 rounded-lg text-4xs font-black uppercase tracking-widest bg-[var(--bg-input)] backdrop-blur-sm text-[var(--text-secondary)] border border-[var(--border-secondary)]">
                          B: {v.width_cm} cm
                        </span>
                      )}
                      {v.weight_kg && (
                        <span className="px-2 py-0.5 rounded-lg text-4xs font-black uppercase tracking-widest bg-[var(--bg-input)] backdrop-blur-sm text-[var(--text-secondary)] border border-[var(--border-secondary)]">
                          {v.weight_kg} kg
                        </span>
                      )}
                    </div>
                    {/* Equipment badges */}
                    {v.equipment && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {EQUIPMENT_KEYS.filter(eq => v.equipment[eq.key]).map(eq => {
                          const EqIcon = eq.icon;
                          const ec = colorClasses(eq.color);
                          return (
                            <span
                              key={eq.key}
                              className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[7px] font-black uppercase tracking-widest ${ec.bg20} ${ec.text400} border ${ec.border30} backdrop-blur-sm`}
                            >
                              <EqIcon size={8} />
                              {t(`profile.garage.${eq.key}`)}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              /* ── No photo: enhanced classic card ── */
              <div className="bg-[var(--bg-surface-alt)] p-4">
                <div className="flex items-start gap-3">
                  {/* Category icon — larger */}
                  {(() => {
                    const cc = colorClasses(meta.color);
                    return (
                      <div className={`flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center ${cc.bg10} border ${cc.border20}`}>
                        <Icon size={28} className={cc.text400} />
                      </div>
                    );
                  })()}

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-[13px] font-black text-[var(--text-primary)] tracking-tight truncate">
                        {v.nickname}
                      </span>
                      {v.is_default && (
                        <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-4xs font-black uppercase tracking-widest bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                          <CheckCircle2 size={9} />
                          {t('profile.garage.active_badge')}
                        </span>
                      )}
                    </div>

                    {v.make_model && (
                      <p className="text-2xs text-[var(--text-secondary)] font-bold truncate">{v.make_model}</p>
                    )}

                    {/* Stats */}
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      <span className="px-2 py-0.5 rounded-lg text-4xs font-black uppercase tracking-widest bg-[var(--bg-surface-alt)] text-[var(--text-secondary)] border border-[var(--border-primary)]">
                        {t(`profile.garage.categories.${v.category}`)}
                      </span>
                      {v.length_cm && (
                        <span className="px-2 py-0.5 rounded-lg text-4xs font-black uppercase tracking-widest bg-[var(--bg-surface-alt)] text-[var(--text-secondary)] border border-[var(--border-primary)]">
                          L: {v.length_cm} cm
                        </span>
                      )}
                      {v.width_cm && (
                        <span className="px-2 py-0.5 rounded-lg text-4xs font-black uppercase tracking-widest bg-[var(--bg-surface-alt)] text-[var(--text-secondary)] border border-[var(--border-primary)]">
                          B: {v.width_cm} cm
                        </span>
                      )}
                      {v.weight_kg && (
                        <span className="px-2 py-0.5 rounded-lg text-4xs font-black uppercase tracking-widest bg-[var(--bg-surface-alt)] text-[var(--text-secondary)] border border-[var(--border-primary)]">
                          {v.weight_kg} kg
                        </span>
                      )}
                    </div>

                    {/* Equipment badges */}
                    {v.equipment && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {EQUIPMENT_KEYS.filter(eq => v.equipment[eq.key]).map(eq => {
                          const EqIcon = eq.icon;
                          const ec = colorClasses(eq.color);
                          return (
                            <span
                              key={eq.key}
                              className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[7px] font-black uppercase tracking-widest ${ec.bg10} ${ec.text400} border ${ec.border20}`}
                            >
                              <EqIcon size={8} />
                              {t(`profile.garage.${eq.key}`)}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ── Road filter chips ── */}
            {v.road_filters && Object.values(v.road_filters).some(Boolean) && (
              <div className="px-4 py-2.5 border-t border-[var(--border-primary)] flex flex-wrap gap-1.5 bg-[var(--bg-surface-alt)]">
                {([
                  { key: 'track' as const, label: t('spot.tractor_road') },
                  { key: 'unclassified' as const, label: t('profile.road_types.unclassified') },
                  { key: 'service' as const, label: t('profile.road_types.service') },
                  { key: 'residential' as const, label: t('profile.road_types.residential') },
                ]).filter(f => v.road_filters?.[f.key]).map(f => (
                  <span
                    key={f.key}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[7px] font-black uppercase tracking-widest bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                  >
                    <Navigation2 size={8} />
                    {f.label}
                  </span>
                ))}
              </div>
            )}

            {/* ── Terrain preference chips ── */}
            {v.terrain_preferences && v.terrain_preferences.length > 0 && (
              <div className="px-4 py-2.5 border-t border-[var(--border-primary)] flex flex-wrap gap-1.5 bg-[var(--bg-surface-alt)]">
                {v.terrain_preferences.map(tp => {
                  const opt = TERRAIN_OPTIONS.find(o => o.key === tp);
                  if (!opt) return null;
                  const TIcon = opt.icon;
                  const tc = colorClasses(opt.color);
                  return (
                    <span
                      key={tp}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[7px] font-black uppercase tracking-widest ${tc.bg10} ${tc.text400} border ${tc.border20}`}
                    >
                      <TIcon size={8} />
                      {t(`profile.garage.terrain.${tp}`)}
                    </span>
                  );
                })}
              </div>
            )}

            {/* ── Description preview ── */}
            {v.description && (
              <div className="px-4 py-2.5 border-t border-[var(--border-primary)] bg-[var(--bg-surface-alt)]">
                <p className="text-2xs text-[var(--text-secondary)] italic line-clamp-2">
                  &ldquo;{v.description}&rdquo;
                </p>
              </div>
            )}

            {/* ── Action buttons row ── */}
            <div className="px-4 py-3 flex items-center gap-2 border-t border-[var(--border-primary)] bg-[var(--bg-surface-alt)]">
              <button
                onClick={() => handleEdit(v)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-all text-4xs font-black uppercase tracking-widest"
              >
                <Pencil size={10} />
                {t('profile.garage.edit_vehicle')}
              </button>
              <button
                onClick={() => handleRetire(v.id)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[var(--bg-surface-alt)] hover:bg-stone-500/20 text-[var(--text-muted)] hover:text-amber-400 transition-all text-4xs font-black uppercase tracking-widest"
              >
                <Archive size={10} />
                {t('profile.garage.retire')}
              </button>
              {!v.is_default && (
                <button
                  onClick={() => handleSetDefault(v.id)}
                  className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600/10 hover:bg-emerald-600/20 border border-emerald-500/20 hover:border-emerald-500/40 text-emerald-400 transition-all text-4xs font-black uppercase tracking-widest"
                >
                  <CheckCircle2 size={10} />
                  {t('profile.garage.set_default')}
                </button>
              )}
              {v.is_default && (
                <button
                  onClick={() => setShowMarkerPreview(showMarkerPreview === v.id ? null : v.id)}
                  className={`ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all text-4xs font-black uppercase tracking-widest ${
                    user.vehicleMarkerEnabled
                      ? 'bg-blue-600/10 border border-blue-500/30 text-blue-400'
                      : 'bg-[var(--bg-surface-alt)] hover:bg-blue-600/10 text-[var(--text-muted)] hover:text-blue-400 border border-[var(--border-primary)]'
                  }`}
                >
                  <Navigation2 size={10} />
                  {t('profile.garage.vehicle_marker')}
                </button>
              )}
            </div>

            {/* GPS marker preview panel */}
            {v.is_default && showMarkerPreview === v.id && (() => {
              const mColor = v.marker_color || getVehicleCategoryColor(v.category);
              const previewBg = isLightColor(mColor) ? '#1e293b' : 'white';
              const isTowable = v.category === 'caravan' || v.category === 'camper_trailer';
              const hasVogntog = isTowable && !!v.tow_vehicle?.make_model;
              return (
              <div className="mx-4 mb-3 p-4 rounded-xl bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] animate-in fade-in slide-in-from-top-1 duration-200">
                <div className="flex items-start gap-4">
                  {/* Preview */}
                  <div className="flex flex-col items-center gap-2">
                    <div className="relative w-16 h-16 flex items-center justify-center">
                      {v.photo_url ? (
                        <div
                          className="w-14 h-14 rounded-full border-[3px] overflow-hidden mx-auto"
                          style={{ borderColor: mColor }}
                        >
                          <img src={v.photo_url} alt="" className="w-full h-full object-cover" />
                        </div>
                      ) : hasVogntog ? (
                        <div
                          className="w-14 h-14 rounded-full border-[3px] flex items-center justify-center mx-auto overflow-hidden"
                          style={{ borderColor: mColor, backgroundColor: previewBg }}
                        >
                          <svg viewBox="0 0 40 50" width={28} height={35} dangerouslySetInnerHTML={{ __html:
                            getTowVehicleAvatar() +
                            '<rect x="19" y="22" width="2" height="4" rx="0.5" fill="#64748b"/><circle cx="20" cy="22.5" r="1.2" fill="#94a3b8"/>' +
                            '<g transform="translate(0,16)">' + getVehicleAvatar(v.category).replace(/FILL/g, mColor) + '</g>'
                          }} />
                        </div>
                      ) : (
                        <div
                          className="w-14 h-14 rounded-full border-[3px] flex items-center justify-center mx-auto overflow-hidden"
                          style={{ borderColor: mColor, backgroundColor: previewBg }}
                        >
                          <svg viewBox="0 0 40 40" width={34} height={34} dangerouslySetInnerHTML={{ __html: getVehicleAvatar(v.category).replace(/FILL/g, mColor) }} />
                        </div>
                      )}
                      {/* Pulse ring effect preview */}
                      <div
                        className="absolute inset-0 rounded-full animate-ping opacity-20"
                        style={{ backgroundColor: mColor }}
                      />
                    </div>
                    <span className="text-4xs font-bold text-[var(--text-muted)] uppercase tracking-widest">
                      {t('profile.garage.vehicle_marker_preview_title')}
                    </span>
                  </div>

                  {/* Info + toggle */}
                  <div className="flex-1 min-w-0">
                    <p className="text-tiny text-[var(--text-secondary)] leading-relaxed mb-3">
                      {t('profile.garage.vehicle_marker_description')}
                    </p>

                    {/* Color picker */}
                    <div className="mb-3">
                      <label className="block text-4xs font-black text-[var(--text-muted)] uppercase tracking-widest mb-1.5">
                        {t('profile.garage.marker_color')}
                      </label>
                      <div className="flex flex-wrap gap-1.5">
                        {/* Default (category) color */}
                        <button
                          onClick={() => handleMarkerColorChange(v.id, null)}
                          className={`w-6 h-6 rounded-full border-2 transition-all ${
                            !v.marker_color ? 'border-white scale-110 shadow-lg' : 'border-transparent opacity-70 hover:opacity-100'
                          }`}
                          style={{ backgroundColor: getVehicleCategoryColor(v.category) }}
                          title={t('profile.garage.marker_color_default')}
                        />
                        {MARKER_COLOR_PRESETS.map(c => (
                          <button
                            key={c}
                            onClick={() => handleMarkerColorChange(v.id, c)}
                            className={`w-6 h-6 rounded-full border-2 transition-all ${
                              v.marker_color === c ? 'border-white scale-110 shadow-lg' : 'border-transparent opacity-70 hover:opacity-100'
                            }`}
                            style={{ backgroundColor: c, ...(isLightColor(c) && v.marker_color !== c ? { outline: '1px solid rgba(255,255,255,0.3)' } : {}) }}
                          />
                        ))}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-green-600/10 border border-green-500/20">
                      <Navigation2 size={12} className="text-green-400" />
                      <span className="text-3xs font-black uppercase tracking-widest text-green-400">
                        {t('profile.garage.vehicle_marker_active')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              );
            })()}

            {/* Tow vehicle section — only for caravan / camper_trailer */}
            {(v.category === 'caravan' || v.category === 'camper_trailer') && (
              <div className="px-4 pb-3">
                <TowVehicleSection vehicle={v} userId={user.uid} allVehicles={vehicles} />
              </div>
            )}

            {/* Delete confirmation */}
            {isConfirmingDelete && (
              <div className="mx-4 mb-3 p-3 rounded-xl bg-red-500/5 border border-red-500/20 animate-in fade-in slide-in-from-top-1 duration-200">
                <p className="text-2xs text-red-400 font-bold mb-2">
                  {t('profile.garage.delete_confirm')}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleDelete(v.id)}
                    className="flex-1 py-2 rounded-xl text-3xs font-black uppercase tracking-widest bg-red-600 hover:bg-red-500 text-white transition-all"
                  >
                    {t('profile.garage.delete_vehicle')}
                  </button>
                  <button
                    onClick={() => setDeleteConfirmId(null)}
                    className="flex-1 py-2 rounded-xl text-3xs font-black uppercase tracking-widest bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] text-[var(--text-secondary)] border border-[var(--border-primary)] transition-all"
                  >
                    {t('profile.garage.cancel')}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* ── Retired vehicles section ── */}
      {vehicles.filter(v => v.is_retired).length > 0 && (
        <>
          <SectionDivider label={t('profile.garage.retired_section')} />
          {vehicles.filter(v => v.is_retired).map((v, index) => {
            const meta = CATEGORY_META[v.category] || CATEGORY_META.motorhome;
            const Icon = meta.icon;
            const isConfirmingDelete = deleteConfirmId === v.id;
            return (
              <div
                key={v.id}
                className="relative overflow-hidden rounded-2xl border border-[var(--border-primary)] opacity-50 transition-all animate-in fade-in duration-300"
                style={{ animationDelay: `${index * 80}ms` }}
              >
                <div className="bg-[var(--bg-surface-alt)] p-4">
                  <div className="flex items-start gap-3">
                    <div className={`flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center bg-stone-500/10 border border-stone-500/20`}>
                      <Icon size={28} className="text-stone-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-[13px] font-black text-[var(--text-primary)] tracking-tight truncate">{v.nickname}</span>
                        <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-4xs font-black uppercase tracking-widest bg-stone-500/10 text-stone-500 border border-stone-500/20">
                          <Archive size={9} />
                          {t('profile.garage.retired_badge')}
                        </span>
                      </div>
                      {v.make_model && (
                        <p className="text-2xs text-[var(--text-secondary)] font-bold truncate">{v.make_model}</p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="px-4 py-3 flex items-center gap-2 border-t border-[var(--border-primary)] bg-[var(--bg-surface-alt)]">
                  <button
                    onClick={() => handleUnretire(v.id)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600/10 hover:bg-emerald-600/20 border border-emerald-500/20 hover:border-emerald-500/40 text-emerald-400 transition-all text-4xs font-black uppercase tracking-widest"
                  >
                    <RotateCcw size={10} />
                    {t('profile.garage.unretire')}
                  </button>
                  <button
                    onClick={() => setDeleteConfirmId(v.id)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[var(--bg-surface-alt)] hover:bg-red-500/20 text-[var(--text-muted)] hover:text-red-400 transition-all text-4xs font-black uppercase tracking-widest"
                  >
                    <Trash2 size={10} />
                  </button>
                </div>
                {isConfirmingDelete && (
                  <div className="mx-4 mb-3 p-3 rounded-xl bg-red-500/5 border border-red-500/20 animate-in fade-in slide-in-from-top-1 duration-200">
                    <p className="text-2xs text-red-400 font-bold mb-2">{t('profile.garage.delete_confirm')}</p>
                    <div className="flex gap-2">
                      <button onClick={() => handleDelete(v.id)} className="flex-1 py-2 rounded-xl text-3xs font-black uppercase tracking-widest bg-red-600 hover:bg-red-500 text-white transition-all">
                        {t('profile.garage.delete_vehicle')}
                      </button>
                      <button onClick={() => setDeleteConfirmId(null)} className="flex-1 py-2 rounded-xl text-3xs font-black uppercase tracking-widest bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] text-[var(--text-secondary)] border border-[var(--border-primary)] transition-all">
                        {t('profile.garage.cancel')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}

      {/* Add Vehicle Form (inline) */}
      {showForm ? (
        <AddVehicleForm
          user={user}
          existingVehicle={editingVehicle}
          vehicleCount={vehicles.length}
          onSaved={handleFormClose}
          onCancel={handleFormClose}
        />
      ) : (
        <button
          onClick={() => { setEditingVehicle(null); setShowForm(true); }}
          className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl text-tiny font-black uppercase tracking-widest bg-emerald-600 hover:bg-emerald-500 text-white transition-all active:scale-[0.98]"
        >
          <Plus size={16} />
          {t('profile.garage.add_vehicle')}
        </button>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════
// ██  AddVehicleForm (inline) — enhanced with photos, terrain, desc
// ══════════════════════════════════════════════════════════════════════

interface AddVehicleFormProps {
  user: AppUser;
  existingVehicle: Vehicle | null;
  vehicleCount: number;
  onSaved: () => void;
  onCancel: () => void;
}

const AddVehicleForm: React.FC<AddVehicleFormProps> = ({
  user, existingVehicle, vehicleCount, onSaved, onCancel,
}) => {
  const { t } = useTranslation();
  const isEditing = !!existingVehicle;

  // Form state
  const [nickname, setNickname] = useState(existingVehicle?.nickname || '');
  const [makeModel, setMakeModel] = useState(existingVehicle?.make_model || '');
  const [category, setCategory] = useState<VehicleCategory>(existingVehicle?.category || 'motorhome');
  const [lengthCm, setLengthCm] = useState(existingVehicle?.length_cm?.toString() || '');
  const [widthCm, setWidthCm] = useState(existingVehicle?.width_cm?.toString() || '');
  const [weightKg, setWeightKg] = useState(existingVehicle?.weight_kg?.toString() || '');
  const [equipment, setEquipment] = useState<VehicleEquipment>(existingVehicle?.equipment || { ...DEFAULT_EQUIPMENT });
  const [regNr, setRegNr] = useState(existingVehicle?.registration_number || '');
  const [description, setDescription] = useState(existingVehicle?.description || '');
  const [terrainPrefs, setTerrainPrefs] = useState<TerrainPreference[]>(existingVehicle?.terrain_preferences || []);
  const [roadFilters, setRoadFilters] = useState(existingVehicle?.road_filters || {
    track: false,
    unclassified: false,
    service: false,
    residential: false,
  });
  const [markerColor, setMarkerColor] = useState(existingVehicle?.marker_color || '');

  // Photo state
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [existingPhotos, setExistingPhotos] = useState<string[]>(existingVehicle?.photo_urls || (existingVehicle?.photo_url ? [existingVehicle.photo_url] : []));
  const [previews, setPreviews] = useState<string[]>([]);

  // UI state
  const [saving, setSaving] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [svvLoading, setSvvLoading] = useState(false);
  const [svvError, setSvvError] = useState<string | null>(null);
  const [svvSuccess, setSvvSuccess] = useState(false);
  const [svvExpanded, setSvvExpanded] = useState(false);
  const [show4x4Tip, setShow4x4Tip] = useState(false);

  const toggleEquipment = (key: keyof VehicleEquipment) => {
    setEquipment(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleTerrain = (key: TerrainPreference) => {
    setTerrainPrefs(prev =>
      prev.includes(key) ? prev.filter(t => t !== key) : [...prev, key]
    );
  };

  // ── Photo handling ────────────────────────────────────────────────

  const handleAddPhoto = async () => {
    if (existingPhotos.length + selectedFiles.length >= 4) return;
    const result = await pickPhoto();
    if (!result) return;
    setSelectedFiles(prev => [...prev, result.file]);
    setPreviews(prev => [...prev, URL.createObjectURL(result.file)]);
  };

  const removeExistingPhoto = (index: number) => {
    setExistingPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const removeNewPhoto = (index: number) => {
    URL.revokeObjectURL(previews[index]);
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const totalPhotos = existingPhotos.length + selectedFiles.length;

  // ── SVV Lookup ─────────────────────────────────────────────────

  const handleSVVLookup = async () => {
    const cleaned = regNr.toUpperCase().replace(/\s/g, '');
    if (!cleaned) return;

    setSvvLoading(true);
    setSvvError(null);
    setSvvSuccess(false);

    try {
      const result = await fetchVehicleDataFromSVV(cleaned);
      if (result.ok) {
        if (result.make_model) setMakeModel(result.make_model);
        if (result.length_cm) setLengthCm(result.length_cm.toString());
        if (result.width_cm) setWidthCm(result.width_cm.toString());
        if (result.weight_kg) setWeightKg(result.weight_kg.toString());
        if (result.is_ev) setEquipment(prev => ({ ...prev, is_ev: true }));
        if (result.is_4wd) setEquipment(prev => ({ ...prev, four_wheel_drive: true }));
        if (result.suggested_category) setCategory(result.suggested_category);
        if (result.color) setMarkerColor(result.color);
        setRegNr(cleaned);
        setSvvSuccess(true);
      }
    } catch (err: any) {
      const code = err?.code || '';
      if (code.includes('not-found')) {
        setSvvError(t('profile.garage.reg_not_found'));
      } else {
        setSvvError(t('profile.garage.reg_lookup_error'));
      }
    } finally {
      setSvvLoading(false);
    }
  };

  // ── Save ───────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!nickname.trim()) return;
    setSaving(true);

    try {
      // Upload new photos if any
      let photoUrls = [...existingPhotos];
      if (selectedFiles.length > 0) {
        setUploadingPhotos(true);
        const vehicleId = existingVehicle?.id || `new_${Date.now()}`;
        const newUrls = await uploadVehiclePhotos(user.uid, vehicleId, selectedFiles);
        photoUrls = [...photoUrls, ...newUrls];
        setUploadingPhotos(false);
      }

      const isFirstVehicle = vehicleCount === 0 && !isEditing;
      const vehicleData = {
        nickname: nickname.trim(),
        make_model: makeModel.trim(),
        registration_number: regNr.trim() || undefined,
        category,
        length_cm: lengthCm ? parseInt(lengthCm, 10) : undefined,
        width_cm: widthCm ? parseInt(widthCm, 10) : undefined,
        weight_kg: weightKg ? parseInt(weightKg, 10) : undefined,
        equipment,
        is_default: isFirstVehicle ? true : (existingVehicle?.is_default || false),
        created_at: existingVehicle?.created_at || Date.now(),
        photo_url: photoUrls[0] || undefined,
        photo_urls: photoUrls.length > 0 ? photoUrls : undefined,
        description: description.trim() || undefined,
        terrain_preferences: terrainPrefs.length > 0 ? terrainPrefs : undefined,
        road_filters: roadFilters,
        marker_color: markerColor || undefined,
      };

      if (isEditing && existingVehicle) {
        await updateVehicle(user.uid, existingVehicle.id, vehicleData);
      } else {
        await addVehicle(user.uid, vehicleData);
      }
      // Show 4x4 tip if user just registered/saved a 4x4 vehicle
      if (equipment.four_wheel_drive) {
        setShow4x4Tip(true);
        return; // Don't call onSaved yet — wait for tip dismissal
      }
      onSaved();
    } catch (err) {
      console.error('Failed to save vehicle:', err);
    } finally {
      setSaving(false);
      setUploadingPhotos(false);
    }
  };

  // ── Input class ────────────────────────────────────────────────

  const inputClass = 'w-full bg-[var(--bg-input)] border border-[var(--border-primary)] rounded-xl px-4 py-2.5 text-[var(--text-primary)] text-[13px] font-bold focus:border-emerald-500/30 outline-none transition-all placeholder:text-[var(--text-muted)]';

  return (
    <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] rounded-2xl p-4 space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-tiny font-black text-[var(--text-primary)] uppercase tracking-widest">
          {isEditing ? t('profile.garage.edit_vehicle') : t('profile.garage.add_vehicle')}
        </h4>
        <button onClick={onCancel} className="p-1.5 rounded-lg bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-all">
          <X size={14} />
        </button>
      </div>

      {/* SVV Lookup — Norwegian flag button, expandable */}
      {!svvExpanded ? (
        <button
          type="button"
          onClick={() => setSvvExpanded(true)}
          className="flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-500/5 border border-blue-500/15 hover:bg-blue-500/10 transition-all group"
        >
          <span className="text-base leading-none">🇳🇴</span>
          <Search size={13} className="text-blue-400" />
          <span className="text-3xs font-black text-blue-400 uppercase tracking-widest">{t('profile.garage.reg_lookup')}</span>
        </button>
      ) : (
        <div className="bg-blue-500/5 border border-blue-500/15 rounded-xl p-3 space-y-2 animate-in fade-in slide-in-from-top-1 duration-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-base leading-none">🇳🇴</span>
              <p className="text-3xs font-black text-blue-400 uppercase tracking-widest">
                {t('profile.garage.reg_lookup')}
              </p>
            </div>
            <button onClick={() => setSvvExpanded(false)} className="p-1 rounded-md hover:bg-[var(--bg-surface-alt)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-all">
              <X size={12} />
            </button>
          </div>
          <p className="text-4xs text-[var(--text-muted)] font-bold">{t('profile.garage.reg_norway_only')}</p>
          <div className="flex gap-2">
            <input
              value={regNr}
              onChange={e => setRegNr(e.target.value.toUpperCase())}
              placeholder={t('profile.garage.reg_placeholder')}
              className="flex-1 bg-[var(--bg-input)] border border-[var(--border-primary)] rounded-lg px-3 py-2 text-[var(--text-primary)] text-[13px] font-bold tracking-wider focus:border-blue-500/30 outline-none transition-all placeholder:text-[var(--text-muted)] uppercase"
              maxLength={8}
            />
            <button
              onClick={handleSVVLookup}
              disabled={svvLoading || !regNr.trim()}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-2xs font-black uppercase tracking-widest transition-all"
            >
              {svvLoading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
              {svvLoading ? t('profile.garage.reg_lookup_loading') : t('profile.garage.reg_lookup_short')}
            </button>
          </div>
          {svvError && (
            <p className="text-3xs font-bold text-red-400">{svvError}</p>
          )}
          {svvSuccess && (
            <p className="text-3xs font-bold text-emerald-400">{t('profile.garage.reg_filled')}</p>
          )}
        </div>
      )}

      {/* ── Section: Vehicle Details ── */}
      <SectionDivider label={t('profile.garage.section_details')} />

      {/* Category selector — larger, 2-column */}
      <div>
        <label className="block text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest mb-2">
          {t('profile.garage.category')}
        </label>
        <div className="grid grid-cols-2 gap-2.5">
          {CATEGORIES.map(cat => {
            const meta = CATEGORY_META[cat];
            const CatIcon = meta.icon;
            const isSelected = category === cat;
            const cc = colorClasses(meta.color);
            return (
              <button
                key={cat}
                onClick={() => {
                  setCategory(cat);
                  if (TRAILER_CATEGORIES.includes(cat)) {
                    setEquipment(prev => ({ ...prev, is_ev: false, four_wheel_drive: false }));
                  }
                }}
                className={`flex flex-col items-center gap-2 py-3.5 px-3 rounded-2xl transition-all border text-center active:scale-95 ${
                  isSelected
                    ? `${cc.bg15} ${cc.text400} ${cc.border30} shadow-lg`
                    : 'bg-[var(--bg-input)] border-[var(--border-primary)] text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
                }`}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all ${
                  isSelected ? cc.bg20 : 'bg-[var(--bg-surface-alt)]'
                }`}>
                  <CatIcon size={24} className={isSelected ? cc.text400 : 'text-[var(--text-muted)]'} />
                </div>
                <span className="text-3xs font-black uppercase tracking-widest">
                  {t(`profile.garage.categories.${cat}`)}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Nickname */}
      <div>
        <label className="block text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest mb-1.5">
          {t('profile.garage.nickname')} *
        </label>
        <input
          value={nickname}
          onChange={e => setNickname(e.target.value)}
          placeholder={t('profile.garage.nickname_placeholder')}
          className={inputClass}
          maxLength={40}
        />
      </div>

      {/* Make / Model */}
      <div>
        <label className="block text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest mb-1.5">
          {t('profile.garage.make_model')}
        </label>
        <input
          value={makeModel}
          onChange={e => setMakeModel(e.target.value)}
          placeholder={t('profile.garage.make_model_placeholder')}
          className={inputClass}
          maxLength={60}
        />
      </div>

      {/* ── Section: Photos & Description ── */}
      <SectionDivider label={t('profile.garage.section_photos')} />

      {/* Photo upload grid */}
      <div>
        <label className="block text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest mb-2">
          {t('profile.garage.photos')}
        </label>
        <div className="grid grid-cols-4 gap-2">
          {/* Existing photos */}
          {existingPhotos.map((url, i) => (
            <div key={`existing-${i}`} className="relative aspect-square rounded-xl overflow-hidden border border-[var(--border-secondary)] group">
              <img src={url} className="w-full h-full object-cover" alt="" />
              <button
                type="button"
                onClick={() => removeExistingPhoto(i)}
                className="absolute top-1 right-1 p-1.5 bg-black/60 text-white rounded-lg sm:opacity-0 sm:group-hover:opacity-100 transition-opacity min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                <X size={14} />
              </button>
              {i === 0 && (
                <span className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-black/60 rounded text-[7px] font-black text-amber-400 uppercase tracking-widest">
                  {t('profile.garage.hero_photo')}
                </span>
              )}
            </div>
          ))}

          {/* New photo previews */}
          {previews.map((url, i) => (
            <div key={`new-${i}`} className="relative aspect-square rounded-xl overflow-hidden border border-emerald-500/20 group">
              <img src={url} className="w-full h-full object-cover" alt="" />
              <button
                type="button"
                onClick={() => removeNewPhoto(i)}
                className="absolute top-1 right-1 p-1.5 bg-black/60 text-white rounded-lg sm:opacity-0 sm:group-hover:opacity-100 transition-opacity min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                <X size={14} />
              </button>
              {existingPhotos.length === 0 && i === 0 && (
                <span className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-black/60 rounded text-[7px] font-black text-amber-400 uppercase tracking-widest">
                  {t('profile.garage.hero_photo')}
                </span>
              )}
            </div>
          ))}

          {/* Upload button */}
          {totalPhotos < 4 && (
            <button
              type="button"
              onClick={handleAddPhoto}
              className="aspect-square bg-[var(--bg-input)] border-2 border-dashed border-[var(--border-secondary)] rounded-xl flex flex-col items-center justify-center gap-1 text-[var(--text-muted)] hover:text-emerald-400 hover:border-emerald-500/30 transition-all active:scale-95"
            >
              <Camera size={18} />
              <span className="text-[7px] font-black uppercase tracking-widest">{t('profile.garage.add_photo')}</span>
            </button>
          )}
        </div>
        <p className="text-4xs text-[var(--text-muted)] mt-1.5 font-bold">
          {t('profile.garage.photos_hint')}
        </p>
      </div>

      {/* Description / brag */}
      <div>
        <label className="block text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest mb-1.5">
          {t('profile.garage.description')}
        </label>
        <textarea
          value={description}
          onChange={e => setDescription(e.target.value.slice(0, 300))}
          placeholder={t('profile.garage.description_placeholder')}
          className="w-full bg-[var(--bg-input)] border border-[var(--border-primary)] rounded-xl px-4 py-2.5 text-[var(--text-primary)] text-[13px] font-bold focus:border-emerald-500/30 outline-none transition-all placeholder:text-[var(--text-muted)] resize-none min-h-[80px]"
          maxLength={300}
        />
        <div className="flex justify-end mt-1">
          <span className={`text-4xs font-bold ${description.length > 250 ? 'text-amber-400' : 'text-[var(--text-muted)]'}`}>
            {description.length}/300
          </span>
        </div>
      </div>

      {/* ── Section: Dimensions & Weight ── */}
      <SectionDivider label={t('profile.garage.section_dimensions')} />

      {/* Dimensions & Weight */}
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest mb-1.5">
            {t('profile.garage.length_cm')}
          </label>
          <input
            type="number"
            value={lengthCm}
            onChange={e => setLengthCm(e.target.value)}
            placeholder="700"
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest mb-1.5">
            {t('profile.garage.width_cm')}
          </label>
          <input
            type="number"
            value={widthCm}
            onChange={e => setWidthCm(e.target.value)}
            placeholder="230"
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest mb-1.5">
            {t('profile.garage.weight_kg')}
          </label>
          <input
            type="number"
            value={weightKg}
            onChange={e => setWeightKg(e.target.value)}
            placeholder="3500"
            className={inputClass}
          />
        </div>
      </div>

      {/* ── Section: Equipment & Features ── */}
      <SectionDivider label={t('profile.garage.section_equipment')} />

      {/* Equipment toggles — filtered by category */}
      <div>
        <div className="space-y-1.5">
          {EQUIPMENT_KEYS.filter(eq => !eq.motorOnly || !TRAILER_CATEGORIES.includes(category)).map(eq => {
            const EqIcon = eq.icon;
            const isOn = equipment[eq.key];
            const ec = colorClasses(eq.color);
            return (
              <div
                key={eq.key}
                className="bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] rounded-xl px-3 py-2.5 flex items-center justify-between"
              >
                <div className="flex items-center gap-2.5">
                  <EqIcon size={14} className={ec.text400} />
                  <span className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">
                    {t(`profile.garage.${eq.key}`)}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => toggleEquipment(eq.key)}
                  className={`w-11 h-6 rounded-full transition-all relative flex-shrink-0 ${
                    isOn ? 'bg-emerald-600' : 'bg-[var(--bg-input)] border border-[var(--border-primary)]'
                  }`}
                >
                  <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all shadow-md ${isOn ? 'left-6' : 'left-1'}`} />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Section: Terrain & Preferences ── */}
      <SectionDivider label={t('profile.garage.section_terrain')} />

      {/* Info banner: spots are filtered by vehicle preferences */}
      <div className="flex items-start gap-2.5 px-3 py-2.5 rounded-xl bg-blue-500/5 border border-blue-500/15">
        <Info size={14} className="text-blue-400 mt-0.5 flex-shrink-0" />
        <p className="text-3xs font-bold text-blue-300/80 leading-relaxed">
          {t('profile.garage.road_filter_info')}
        </p>
      </div>

      {/* Road type filter toggles */}
      <div>
        <p className="text-4xs text-[var(--text-muted)] font-bold mb-2">
          {t('profile.garage.road_filter_hint')}
        </p>
        <div className="grid grid-cols-2 gap-2">
          {[
            { key: 'track' as const, label: t('spot.tractor_road') },
            { key: 'unclassified' as const, label: t('profile.road_types.unclassified') },
            { key: 'service' as const, label: t('profile.road_types.service') },
            { key: 'residential' as const, label: t('profile.road_types.residential') },
          ].map(filter => (
            <button
              key={filter.key}
              type="button"
              onClick={() => setRoadFilters(prev => ({ ...prev, [filter.key]: !prev[filter.key] }))}
              className={`py-2 rounded-xl text-3xs font-black uppercase tracking-widest transition-all border active:scale-95 ${
                roadFilters[filter.key]
                  ? 'bg-emerald-600/20 text-emerald-400 border-emerald-500/30'
                  : 'bg-[var(--bg-input)] border-[var(--border-primary)] text-[var(--text-muted)]'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
        {roadFilters.track && (
          <div className="flex items-start gap-2 px-1 mt-2 animate-in fade-in slide-in-from-top-1 duration-200">
            <AlertTriangle size={12} className="text-amber-400 mt-0.5 flex-shrink-0" />
            <p className="text-3xs font-bold text-amber-400/80 leading-relaxed">{t('profile.road_types.track_warning')}</p>
          </div>
        )}
      </div>

      {/* Terrain preference chips */}
      <div>
        <p className="text-4xs text-[var(--text-muted)] font-bold mb-2">
          {t('profile.garage.terrain_hint')}
        </p>
        <div className="flex flex-wrap gap-2">
          {TERRAIN_OPTIONS.map(opt => {
            const TIcon = opt.icon;
            const isSelected = terrainPrefs.includes(opt.key);
            const tc = colorClasses(opt.color);
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => toggleTerrain(opt.key)}
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-3xs font-black uppercase tracking-widest transition-all border active:scale-95 ${
                  isSelected
                    ? `${tc.bg15} ${tc.text400} ${tc.border30}`
                    : 'bg-[var(--bg-surface-alt)] text-[var(--text-muted)] border-[var(--border-primary)] hover:border-[var(--border-secondary)]'
                }`}
              >
                <TIcon size={13} />
                {t(`profile.garage.terrain.${opt.key}`)}
              </button>
            );
          })}
        </div>
      </div>

      {/* First vehicle hint */}
      {vehicleCount === 0 && !isEditing && (
        <p className="text-3xs text-emerald-400 font-bold text-center">
          {t('profile.garage.first_vehicle_default')}
        </p>
      )}

      {/* Save / Cancel */}
      <div className="flex gap-2 pt-1">
        <button
          onClick={handleSave}
          disabled={saving || !nickname.trim()}
          className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl text-tiny font-black uppercase tracking-widest bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white transition-all active:scale-[0.98]"
        >
          {saving && <Loader2 size={14} className="animate-spin" />}
          {uploadingPhotos
            ? t('profile.garage.uploading_photos')
            : saving
              ? t('profile.garage.saving')
              : t('profile.garage.save')
          }
        </button>
        <button
          onClick={onCancel}
          className="px-5 py-3 rounded-2xl text-tiny font-black uppercase tracking-widest bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] text-[var(--text-secondary)] border border-[var(--border-primary)] transition-all"
        >
          {t('profile.garage.cancel')}
        </button>
      </div>

      {/* 4x4 tip popup */}
      {show4x4Tip && (
        <div className="fixed inset-0 z-[10050] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => { setShow4x4Tip(false); onSaved(); }} />
          <div className="relative bg-[var(--bg-surface)] border border-[var(--border-secondary)] rounded-2xl p-6 max-w-sm w-full shadow-2xl space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-amber-500/15 border border-amber-500/30">
                <TreePine size={22} className="text-amber-500" />
              </div>
              <h3 className="text-[13px] font-black text-[var(--text-primary)] uppercase tracking-wide">
                {t('profile.garage.offroad_tip_title', '4x4 registrert!')}
              </h3>
            </div>
            <p className="text-[12px] text-[var(--text-secondary)] leading-relaxed">
              {t('profile.garage.offroad_tip_body', 'For å se fricamp-plasser langs skogs- og traktorveier, aktiver kartlagene «Skogsbilveger» og «Traktorveger & Stier» i kartlagmenyen (lag-ikon øverst til høyre på kartet).')}
            </p>
            <button
              onClick={() => { setShow4x4Tip(false); onSaved(); }}
              className="w-full py-3 rounded-xl bg-amber-500 text-black text-tiny font-black uppercase tracking-widest hover:bg-amber-400 transition-colors"
            >
              {t('common.understood', 'Forstått')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════
// ██  TowVehicleSection (vogntog — only for caravan/camper_trailer)
// ══════════════════════════════════════════════════════════════════════

const TowVehicleSection: React.FC<{ vehicle: Vehicle; userId: string; allVehicles: Vehicle[] }> = ({ vehicle, userId, allVehicles }) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState(false);

  const hasTow = !!(vehicle.tow_vehicle?.make_model || vehicle.tow_vehicle?.registration_number || vehicle.tow_vehicle?.length_cm);

  const handleRemove = async () => {
    setRemoving(true);
    try {
      await removeTowVehicle(userId, vehicle.id);
    } catch (err) {
      console.error('Failed to remove tow vehicle:', err);
    } finally {
      setRemoving(false);
    }
  };

  // Editing / adding form
  if (editing) {
    return (
      <TowVehicleForm
        vehicle={vehicle}
        userId={userId}
        allVehicles={allVehicles}
        onDone={() => setEditing(false)}
      />
    );
  }

  // No tow vehicle — show "Add tow vehicle" button
  if (!hasTow) {
    return (
      <button
        onClick={() => setEditing(true)}
        className="mt-3 w-full py-2 rounded-xl text-3xs font-black uppercase tracking-widest bg-amber-500/5 hover:bg-amber-500/10 border border-amber-500/15 hover:border-amber-500/30 text-amber-400 transition-all flex items-center justify-center gap-2"
      >
        <Link2 size={11} />
        {t('profile.garage.add_tow_vehicle')}
      </button>
    );
  }

  // Has tow vehicle — show summary + combined dimensions
  const tow = vehicle.tow_vehicle!;
  const combinedLength = (tow.length_cm && vehicle.length_cm)
    ? tow.length_cm + vehicle.length_cm : null;
  const combinedWidth = (tow.width_cm || vehicle.width_cm)
    ? Math.max(tow.width_cm || 0, vehicle.width_cm || 0) : null;
  const combinedWeight = (tow.weight_kg || vehicle.weight_kg)
    ? (tow.weight_kg || 0) + (vehicle.weight_kg || 0) : null;

  return (
    <div className="mt-3 bg-amber-500/5 border border-amber-500/15 rounded-xl p-3 space-y-2 animate-in fade-in duration-200">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Car size={12} className="text-amber-400" />
          <span className="text-3xs font-black text-amber-400 uppercase tracking-widest">
            {t('profile.garage.tow_vehicle')}
          </span>
        </div>
        <div className="flex gap-1">
          <button
            onClick={() => setEditing(true)}
            className="p-1 rounded-md hover:bg-[var(--bg-surface-alt)] text-[var(--text-muted)] hover:text-amber-400 transition-all"
          >
            <Pencil size={10} />
          </button>
          <button
            onClick={handleRemove}
            disabled={removing}
            className="p-1 rounded-md hover:bg-red-500/10 text-[var(--text-muted)] hover:text-red-400 transition-all disabled:opacity-50"
          >
            <X size={10} />
          </button>
        </div>
      </div>

      {/* Tow vehicle info */}
      <div className="flex items-center gap-2">
        {tow.photo_url && (
          <div className="w-10 h-10 rounded-lg overflow-hidden border border-amber-500/20 flex-shrink-0">
            <img src={tow.photo_url} alt="" className="w-full h-full object-cover" />
          </div>
        )}
        <div>
          {tow.make_model && (
            <span className="text-2xs text-[var(--text-secondary)] font-bold block">{tow.make_model}</span>
          )}
          {tow.registration_number && (
            <span className="px-1.5 py-0.5 rounded text-4xs font-black text-[var(--text-muted)] bg-[var(--bg-surface-alt)] tracking-wider">
              {tow.registration_number}
            </span>
          )}
        </div>
      </div>

      {/* Combined dimensions — "VOGNTOG" */}
      {(combinedLength || combinedWidth || combinedWeight) && (
        <div>
          <p className="text-4xs font-black text-amber-500/60 uppercase tracking-widest mb-1">
            {t('profile.garage.road_train')}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {combinedLength && (
              <span className="px-2 py-0.5 rounded-lg text-4xs font-black uppercase tracking-widest bg-amber-500/10 text-amber-400 border border-amber-500/20">
                L: {combinedLength} cm
              </span>
            )}
            {combinedWidth && (
              <span className="px-2 py-0.5 rounded-lg text-4xs font-black uppercase tracking-widest bg-amber-500/10 text-amber-400 border border-amber-500/20">
                B: {combinedWidth} cm
              </span>
            )}
            {combinedWeight && (
              <span className="px-2 py-0.5 rounded-lg text-4xs font-black uppercase tracking-widest bg-amber-500/10 text-amber-400 border border-amber-500/20">
                {combinedWeight} kg
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════
// ██  TowVehicleForm (inline mini-form for tow vehicle)
// ══════════════════════════════════════════════════════════════════════

const TowVehicleForm: React.FC<{
  vehicle: Vehicle;
  userId: string;
  allVehicles: Vehicle[];
  onDone: () => void;
}> = ({ vehicle, userId, allVehicles, onDone }) => {
  const { t } = useTranslation();
  const existing = vehicle.tow_vehicle;

  const [makeModel, setMakeModel] = useState(existing?.make_model || '');
  const [regNr, setRegNr] = useState(existing?.registration_number || '');
  const [lengthCm, setLengthCm] = useState(existing?.length_cm?.toString() || '');
  const [widthCm, setWidthCm] = useState(existing?.width_cm?.toString() || '');
  const [weightKg, setWeightKg] = useState(existing?.weight_kg?.toString() || '');
  const [isEv, setIsEv] = useState(existing?.is_ev || false);
  const [fourWd, setFourWd] = useState(existing?.four_wheel_drive || false);
  const [existingPhotoUrl, setExistingPhotoUrl] = useState(existing?.photo_url || '');
  const [newPhotoFile, setNewPhotoFile] = useState<File | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [svvLoading, setSvvLoading] = useState(false);
  const [svvError, setSvvError] = useState<string | null>(null);
  const [svvSuccess, setSvvSuccess] = useState(false);
  const [svvExpanded, setSvvExpanded] = useState(false);

  const garageVehicles = allVehicles.filter(gv =>
    gv.id !== vehicle.id &&
    !gv.is_retired &&
    gv.category !== 'caravan' &&
    gv.category !== 'camper_trailer'
  );

  const applyGarageVehicle = (gv: Vehicle) => {
    setMakeModel(gv.make_model || '');
    setRegNr(gv.registration_number || '');
    setLengthCm(gv.length_cm?.toString() || '');
    setWidthCm(gv.width_cm?.toString() || '');
    setWeightKg(gv.weight_kg?.toString() || '');
    setIsEv(gv.equipment?.is_ev || false);
    setFourWd(gv.equipment?.four_wheel_drive || false);
    setExistingPhotoUrl(gv.photo_url || '');
    setNewPhotoFile(null);
  };

  const handleSVVLookup = async () => {
    const cleaned = regNr.toUpperCase().replace(/\s/g, '');
    if (!cleaned) return;
    setSvvLoading(true);
    setSvvError(null);
    setSvvSuccess(false);
    try {
      const result = await fetchVehicleDataFromSVV(cleaned);
      if (result.ok) {
        if (result.make_model) setMakeModel(result.make_model);
        if (result.length_cm) setLengthCm(result.length_cm.toString());
        if (result.width_cm) setWidthCm(result.width_cm.toString());
        if (result.weight_kg) setWeightKg(result.weight_kg.toString());
        if (result.is_ev) setIsEv(true);
        if (result.is_4wd) setFourWd(true);
        setRegNr(cleaned);
        setSvvSuccess(true);
      }
    } catch (err: any) {
      const code = err?.code || '';
      setSvvError(code.includes('not-found')
        ? t('profile.garage.reg_not_found')
        : t('profile.garage.reg_lookup_error'));
    } finally {
      setSvvLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      let photoUrl = existingPhotoUrl || undefined;
      if (newPhotoFile) {
        const urls = await uploadVehiclePhotos(userId, `${vehicle.id}_tow`, [newPhotoFile]);
        if (urls.length > 0) photoUrl = urls[0];
      }
      const towData: TowVehicleInfo = {};
      if (regNr.trim()) towData.registration_number = regNr.trim();
      if (makeModel.trim()) towData.make_model = makeModel.trim();
      if (lengthCm) towData.length_cm = parseInt(lengthCm, 10);
      if (widthCm) towData.width_cm = parseInt(widthCm, 10);
      if (weightKg) towData.weight_kg = parseInt(weightKg, 10);
      if (isEv) towData.is_ev = true;
      if (fourWd) towData.four_wheel_drive = true;
      if (photoUrl) towData.photo_url = photoUrl;
      await updateTowVehicle(userId, vehicle.id, towData);
      onDone();
    } catch (err) {
      console.error('Failed to save tow vehicle:', err);
      setSaveError(t('profile.garage.tow_save_error'));
    } finally {
      setSaving(false);
    }
  };

  const inputClass = 'w-full bg-[var(--bg-input)] border border-[var(--border-primary)] rounded-xl px-3 py-2 text-[var(--text-primary)] text-[12px] font-bold focus:border-amber-500/30 outline-none transition-all placeholder:text-[var(--text-muted)]';

  return (
    <div className="mt-3 bg-amber-500/5 border border-amber-500/15 rounded-xl p-3 space-y-3 animate-in fade-in slide-in-from-top-1 duration-200">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Car size={12} className="text-amber-400" />
          <span className="text-3xs font-black text-amber-400 uppercase tracking-widest">
            {t('profile.garage.tow_vehicle')}
          </span>
        </div>
        <button onClick={onDone} className="p-1 rounded-md hover:bg-[var(--bg-surface-alt)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-all">
          <X size={12} />
        </button>
      </div>

      {/* Velg fra garasje */}
      {garageVehicles.length > 0 && (
        <div>
          <p className="text-4xs font-black text-[var(--text-muted)] uppercase tracking-widest mb-1.5">
            {t('profile.garage.tow_select_from_garage')}
          </p>
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {garageVehicles.map(gv => {
              const gvMeta = CATEGORY_META[gv.category] || CATEGORY_META.car;
              const GvIcon = gvMeta.icon;
              return (
                <button
                  key={gv.id}
                  type="button"
                  onClick={() => applyGarageVehicle(gv)}
                  className="flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] hover:border-amber-500/40 hover:bg-amber-500/5 transition-all"
                >
                  <GvIcon size={11} className="text-[var(--text-secondary)]" />
                  <span className="text-4xs font-bold text-[var(--text-secondary)] whitespace-nowrap">{gv.nickname}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* SVV Lookup */}
      {!svvExpanded ? (
        <button
          type="button"
          onClick={() => setSvvExpanded(true)}
          className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-blue-500/5 border border-blue-500/15 hover:bg-blue-500/10 transition-all"
        >
          <span className="text-sm leading-none">🇳🇴</span>
          <Search size={11} className="text-blue-400" />
          <span className="text-4xs font-black text-blue-400 uppercase tracking-widest">{t('profile.garage.reg_lookup')}</span>
        </button>
      ) : (
        <div className="bg-blue-500/5 border border-blue-500/15 rounded-lg p-2.5 space-y-1.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <span className="text-sm leading-none">🇳🇴</span>
              <p className="text-4xs font-black text-blue-400 uppercase tracking-widest">{t('profile.garage.reg_lookup')}</p>
            </div>
            <button onClick={() => setSvvExpanded(false)} className="p-0.5 rounded hover:bg-[var(--bg-surface-alt)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-all">
              <X size={10} />
            </button>
          </div>
          <div className="flex gap-1.5">
            <input
              value={regNr}
              onChange={e => setRegNr(e.target.value.toUpperCase())}
              placeholder={t('profile.garage.reg_placeholder')}
              className="flex-1 bg-[var(--bg-input)] border border-[var(--border-primary)] rounded-lg px-2.5 py-1.5 text-[var(--text-primary)] text-[12px] font-bold tracking-wider focus:border-blue-500/30 outline-none placeholder:text-[var(--text-muted)] uppercase"
              maxLength={8}
            />
            <button
              onClick={handleSVVLookup}
              disabled={svvLoading || !regNr.trim()}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-3xs font-black uppercase tracking-widest transition-all"
            >
              {svvLoading ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
              {svvLoading ? '...' : t('profile.garage.reg_lookup_short')}
            </button>
          </div>
          {svvError && <p className="text-4xs font-bold text-red-400">{svvError}</p>}
          {svvSuccess && <p className="text-4xs font-bold text-emerald-400">{t('profile.garage.reg_filled')}</p>}
        </div>
      )}

      {/* Make / Model */}
      <div>
        <label className="block text-4xs font-black text-[var(--text-muted)] uppercase tracking-widest mb-1">
          {t('profile.garage.make_model')}
        </label>
        <input
          value={makeModel}
          onChange={e => setMakeModel(e.target.value)}
          placeholder={t('profile.garage.make_model_placeholder')}
          className={inputClass}
          maxLength={60}
        />
      </div>

      {/* Dimensions */}
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="block text-4xs font-black text-[var(--text-muted)] uppercase tracking-widest mb-1">
            {t('profile.garage.length_cm')}
          </label>
          <input type="number" value={lengthCm} onChange={e => setLengthCm(e.target.value)} placeholder="450" className={inputClass} />
        </div>
        <div>
          <label className="block text-4xs font-black text-[var(--text-muted)] uppercase tracking-widest mb-1">
            {t('profile.garage.width_cm')}
          </label>
          <input type="number" value={widthCm} onChange={e => setWidthCm(e.target.value)} placeholder="190" className={inputClass} />
        </div>
        <div>
          <label className="block text-4xs font-black text-[var(--text-muted)] uppercase tracking-widest mb-1">
            {t('profile.garage.weight_kg')}
          </label>
          <input type="number" value={weightKg} onChange={e => setWeightKg(e.target.value)} placeholder="2200" className={inputClass} />
        </div>
      </div>

      {/* EV + 4WD toggles */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setIsEv(!isEv)}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-4xs font-black uppercase tracking-widest transition-all border ${
            isEv ? 'bg-blue-500/15 text-blue-400 border-blue-500/30' : 'bg-[var(--bg-surface-alt)] text-[var(--text-muted)] border-[var(--border-primary)]'
          }`}
        >
          <Zap size={11} /> {t('profile.garage.is_ev')}
        </button>
        <button
          type="button"
          onClick={() => setFourWd(!fourWd)}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-4xs font-black uppercase tracking-widest transition-all border ${
            fourWd ? 'bg-amber-500/15 text-amber-400 border-amber-500/30' : 'bg-[var(--bg-surface-alt)] text-[var(--text-muted)] border-[var(--border-primary)]'
          }`}
        >
          <Mountain size={11} /> {t('profile.garage.four_wheel_drive')}
        </button>
      </div>

      {/* Tow vehicle photo */}
      <div>
        <label className="block text-4xs font-black text-[var(--text-muted)] uppercase tracking-widest mb-1">
          {t('profile.garage.tow_photo')}
        </label>
        <div className="flex items-center gap-2">
          {(newPhotoFile || existingPhotoUrl) ? (
            <div className="relative w-16 h-16 rounded-xl overflow-hidden border border-amber-500/20">
              <img
                src={newPhotoFile ? URL.createObjectURL(newPhotoFile) : existingPhotoUrl}
                alt=""
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={() => { setNewPhotoFile(null); setExistingPhotoUrl(''); }}
                className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-black/60 flex items-center justify-center text-white"
              >
                <X size={8} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={async () => {
                const result = await pickPhoto();
                if (result) setNewPhotoFile(result.file);
              }}
              className="w-16 h-16 rounded-xl border-2 border-dashed border-amber-500/20 flex flex-col items-center justify-center gap-1 text-amber-500/40 hover:text-amber-400 hover:border-amber-500/40 transition-all"
            >
              <Camera size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Save error */}
      {saveError && (
        <p className="text-4xs font-bold text-red-400">{saveError}</p>
      )}

      {/* Save / Cancel */}
      <div className="flex gap-2">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-2xs font-black uppercase tracking-widest bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white transition-all"
        >
          {saving && <Loader2 size={12} className="animate-spin" />}
          {saving ? t('profile.garage.saving') : t('profile.garage.save')}
        </button>
        <button
          onClick={onDone}
          className="px-4 py-2.5 rounded-xl text-2xs font-black uppercase tracking-widest bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] text-[var(--text-secondary)] border border-[var(--border-primary)] transition-all"
        >
          {t('profile.garage.cancel')}
        </button>
      </div>
    </div>
  );
};

export default GarageTab;
